import type { Project, ProjectMetadata, CanvasSettings } from '../model/project.js';
import { PROJECT_FORMAT_VERSION } from '../model/project.js';
import type { ComponentInstance } from '../model/component.js';
import type { PinState, BendState } from '../model/pin.js';
import type { ComponentDef } from '../library/component-def.js';
import type { ComponentLibrary } from '../library/loader.js';
import type { BodyRotation } from '../model/types.js';
import type { Wire } from '../model/wire.js';

/**
 * Serialización / deserialización de proyectos a JSON autónomo.
 *
 * Formato ".dbe" (envelope JSON, extensión propia):
 *   {
 *     format: "deadbug-project",
 *     format_version: N,       // 2 = actual, con claves abreviadas
 *     saved_at: ISO,
 *     project: <compact-project>,
 *     defs:    [<ComponentDef>...]   // en su esquema original,
 *                                     // reutilizado directamente por la
 *                                     // biblioteca al abrir.
 *   }
 *
 * Nomenclatura corta dentro de `project` para reducir tamaño en
 * proyectos con muchos pines / dobleces (una `BendState` pasa de
 * ~60 chars a un simple número). Ver mapa {@link COMPACT_KEYS}
 * al final del fichero para la correspondencia.
 *
 * Lectura retro-compatible: la versión 1 (con claves largas
 * originales) se sigue admitiendo.
 */

export const SERIALIZED_FORMAT = 'deadbug-project' as const;
export const SERIALIZED_FORMAT_VERSION = 2 as const;

export interface SerializedProject {
  format: typeof SERIALIZED_FORMAT;
  format_version: number;
  saved_at: string;
  project: unknown; // compact o legacy según format_version
  defs: ComponentDef[];
}

// ---------- API pública ----------

/** Serializa un `Project` con embed de las defs usadas. */
export function serializeProject(project: Project, library: ComponentLibrary): SerializedProject {
  const usedIds = new Set<string>();
  for (const inst of project.components) usedIds.add(inst.def_id);
  const defs: ComponentDef[] = [];
  for (const id of usedIds) {
    const def = library.get(id);
    if (def) defs.push(def);
    else console.warn(`serializeProject: definición no cacheada para "${id}", se omite`);
  }
  return {
    format: SERIALIZED_FORMAT,
    format_version: SERIALIZED_FORMAT_VERSION,
    saved_at: new Date().toISOString(),
    project: encodeProject(project),
    defs,
  };
}

/** Parsea + valida + inyecta defs + devuelve `Project` reconstruido. */
export function deserializeProject(input: unknown, library: ComponentLibrary): Project {
  const raw = typeof input === 'string' ? parseJson(input) : input;
  if (!isRecord(raw)) throw new SerializationError('El fichero no es JSON válido.');
  if (raw['format'] !== SERIALIZED_FORMAT) {
    throw new SerializationError(`Formato desconocido: ${String(raw['format'])}`);
  }
  const version = raw['format_version'];
  if (typeof version !== 'number' || version > SERIALIZED_FORMAT_VERSION) {
    throw new SerializationError(
      `Versión del fichero (${String(version)}) más nueva que la soportada (${SERIALIZED_FORMAT_VERSION}). Actualiza el editor.`,
    );
  }
  const defs = raw['defs'];
  if (Array.isArray(defs)) {
    for (const def of defs) if (isComponentDef(def)) library.injectDef(def);
  }
  const projectRaw = raw['project'];
  if (!isRecord(projectRaw)) {
    throw new SerializationError('El fichero no contiene un proyecto válido.');
  }
  const project = version === 1 ? decodeLegacyProject(projectRaw) : decodeProject(projectRaw);
  // Padding de bends por pin: en el formato compacto se omite el array
  // completo si todos los bends son 0, pero las operaciones bend/cut/
  // restore esperan `pin.bends.length === def.pin.bend_points_count`.
  // Sin este relleno, un pin recuperado no se puede doblar.
  for (const inst of project.components) {
    const def = library.get(inst.def_id);
    if (!def) continue;
    for (let i = 0; i < inst.pins.length; i++) {
      const pinState = inst.pins[i];
      const pinDef = def.pins[i];
      if (!pinState || !pinDef) continue;
      const target = pinDef.bend_points_count;
      while (pinState.bends.length < target) {
        pinState.bends.push({ angle_in_plane_deg: 0, angle_out_of_plane_deg: 0 });
      }
      if (pinState.bends.length > target) pinState.bends.length = target;
    }
  }
  return project;
}

export class SerializationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'SerializationError';
  }
}

// ---------- ENCODE (Project → compact) ----------

interface CompactProject {
  v: number;                     // PROJECT_FORMAT_VERSION
  m: CompactMeta;                // metadata
  cv?: CanvasSettings;           // canvas (omitido si es el default)
  cs: CompactInstance[];         // components
  ws?: CompactWire[];            // wires (omitido si vacío)
  sj?: unknown[];                // solder_joints (omitido si vacío)
  ed?: ComponentDef[];           // embedded_component_defs (raro)
}
interface CompactWire {
  u: string;                     // uid
  f: [number, number];           // from as tuple (x, y)
  dd: number;                    // direction_deg
  bc: number;                    // bend_points_count
  b?: (number | [number, number])[]; // bends (omitido si TODOS 0)
  c?: number;                    // cut_at_index (omitido si null)
  z?: number;                    // z_order (omitido si 0)
}
interface CompactMeta {
  n: string;                     // name
  c: string;                     // created_at
  m: string;                     // modified_at
  a?: string;                    // author
  d?: string;                    // description
}
interface CompactInstance {
  u: string;                     // uid
  d: string;                     // def_id
  p: [number, number];           // position_mm as tuple
  r?: BodyRotation;              // rotation_deg (omitido si 0)
  z?: number;                    // z_order (omitido si 0)
  f?: 1;                         // flipped (omitido si false)
  v?: string;                    // value
  l?: string;                    // label
  pr?: Record<string, string>;   // properties
  ao?: unknown;                  // appearance_override (raw)
  pn: CompactPin[];              // pins
}
interface CompactPin {
  id: string;                    // pin_id
  b?: (number | [number, number])[]; // bends; number = in-plane (out=0)
  c?: number;                    // cut_at_index (omitido si null)
  dl?: number;                   // length_adjust_mm
  da?: number;                   // angle_adjust_deg
  dab?: number;                  // angle_adjust_at_bend
  ao?: Record<string, number>;   // align_offsets
}

function encodeProject(p: Project): CompactProject {
  const out: CompactProject = {
    v: p.format_version,
    m: encodeMeta(p.metadata),
    cs: p.components.map(encodeInstance),
  };
  if (!isDefaultCanvas(p.canvas)) out.cv = p.canvas;
  if (p.wires && p.wires.length > 0) out.ws = p.wires.map(encodeWire);
  if (p.solder_joints && p.solder_joints.length > 0) out.sj = p.solder_joints;
  if (p.embedded_component_defs && p.embedded_component_defs.length > 0) {
    out.ed = p.embedded_component_defs;
  }
  return out;
}

function encodeWire(w: Wire): CompactWire {
  const out: CompactWire = {
    u: w.uid,
    f: [w.from.x, w.from.y],
    dd: w.direction_deg,
    bc: w.bend_points_count,
  };
  const bends = w.bends.map(encodeBend);
  if (bends.some((v) => v !== 0)) out.b = bends;
  if (w.cut_at_index !== null && w.cut_at_index !== undefined) out.c = w.cut_at_index;
  if (w.z_order) out.z = w.z_order;
  return out;
}

function encodeMeta(m: ProjectMetadata): CompactMeta {
  const out: CompactMeta = { n: m.name, c: m.created_at, m: m.modified_at };
  if (m.author) out.a = m.author;
  if (m.description) out.d = m.description;
  return out;
}

function encodeInstance(inst: ComponentInstance): CompactInstance {
  const out: CompactInstance = {
    u: inst.uid,
    d: inst.def_id,
    p: [inst.position_mm.x, inst.position_mm.y],
    pn: inst.pins.map(encodePin),
  };
  if (inst.rotation_deg) out.r = inst.rotation_deg;
  if (inst.z_order) out.z = inst.z_order;
  if (inst.flipped) out.f = 1;
  if (inst.value !== undefined) out.v = inst.value;
  if (inst.label !== undefined) out.l = inst.label;
  if (inst.properties && Object.keys(inst.properties).length > 0) out.pr = inst.properties;
  if (inst.appearance_override) out.ao = inst.appearance_override;
  return out;
}

function encodePin(pin: PinState): CompactPin {
  const out: CompactPin = { id: pin.pin_id };
  const bends = pin.bends.map(encodeBend);
  // Si TODOS los bends son 0, no se emite el array (queda implícito).
  if (bends.some((v) => v !== 0)) out.b = bends;
  if (pin.cut_at_index !== null && pin.cut_at_index !== undefined) out.c = pin.cut_at_index;
  if (pin.length_adjust_mm) out.dl = pin.length_adjust_mm;
  if (pin.angle_adjust_deg) out.da = pin.angle_adjust_deg;
  if (pin.angle_adjust_at_bend !== undefined) out.dab = pin.angle_adjust_at_bend;
  if (pin.align_offsets && Object.keys(pin.align_offsets).length > 0) {
    // Record<number,number> → JSON no acepta claves numéricas; se
    // guarda como Record<string,number> igualmente representable.
    out.ao = Object.fromEntries(
      Object.entries(pin.align_offsets).map(([k, v]) => [String(k), v]),
    );
  }
  return out;
}

function encodeBend(b: BendState): number | [number, number] {
  if (b.angle_out_of_plane_deg) return [b.angle_in_plane_deg, b.angle_out_of_plane_deg];
  return b.angle_in_plane_deg;
}

// ---------- DECODE (compact → Project) ----------

function decodeProject(raw: Record<string, unknown>): Project {
  const meta = decodeMeta(raw['m']);
  const canvas = isRecord(raw['cv']) ? (raw['cv'] as unknown as CanvasSettings) : defaultCanvas();
  const components = Array.isArray(raw['cs'])
    ? (raw['cs'] as unknown[]).map(decodeInstance)
    : [];
  const wires = Array.isArray(raw['ws']) ? (raw['ws'] as unknown[]).map(decodeWire) : [];
  const solder_joints = Array.isArray(raw['sj']) ? (raw['sj'] as unknown[]) : [];
  const embedded = Array.isArray(raw['ed']) ? (raw['ed'] as ComponentDef[]) : undefined;
  const project: Project = {
    format_version: PROJECT_FORMAT_VERSION,
    metadata: meta,
    canvas,
    components,
    solder_joints: solder_joints as Project['solder_joints'],
  };
  if (wires.length > 0) project.wires = wires;
  if (embedded) project.embedded_component_defs = embedded;
  return project;
}

function decodeWire(raw: unknown): Wire {
  if (!isRecord(raw)) throw new SerializationError('Cable inválido en el proyecto.');
  const f = raw['f'];
  const from = Array.isArray(f) && typeof f[0] === 'number' && typeof f[1] === 'number'
    ? { x: f[0] as number, y: f[1] as number }
    : { x: 0, y: 0 };
  const bc = typeof raw['bc'] === 'number' ? raw['bc'] : 0;
  const bendsIn = Array.isArray(raw['b']) ? (raw['b'] as unknown[]) : [];
  const bends: BendState[] = bendsIn.map(decodeBend);
  // Padding: si `bends` se omitió por ser todo cero, la longitud
  // recibida es 0; la restauramos para que coincida con bc y que las
  // operaciones bend/cut funcionen (esperan bends[i]).
  while (bends.length < bc) bends.push({ angle_in_plane_deg: 0, angle_out_of_plane_deg: 0 });
  bends.length = bc;
  return {
    uid: strOr(raw['u'], ''),
    from,
    direction_deg: typeof raw['dd'] === 'number' ? raw['dd'] : 0,
    bend_points_count: bc,
    bends,
    cut_at_index: typeof raw['c'] === 'number' ? raw['c'] : null,
    z_order: typeof raw['z'] === 'number' ? raw['z'] : 0,
  };
}

function decodeMeta(raw: unknown): ProjectMetadata {
  if (!isRecord(raw)) return { name: 'Untitled', created_at: nowIso(), modified_at: nowIso() };
  const out: ProjectMetadata = {
    name: strOr(raw['n'], 'Untitled'),
    created_at: strOr(raw['c'], nowIso()),
    modified_at: strOr(raw['m'], nowIso()),
  };
  if (typeof raw['a'] === 'string') out.author = raw['a'];
  if (typeof raw['d'] === 'string') out.description = raw['d'];
  return out;
}

function decodeInstance(raw: unknown): ComponentInstance {
  if (!isRecord(raw)) throw new SerializationError('Componente inválido en el proyecto.');
  const p = raw['p'];
  const pos = Array.isArray(p) && typeof p[0] === 'number' && typeof p[1] === 'number'
    ? { x: p[0] as number, y: p[1] as number }
    : { x: 0, y: 0 };
  const inst: ComponentInstance = {
    uid: strOr(raw['u'], ''),
    def_id: strOr(raw['d'], ''),
    position_mm: pos,
    rotation_deg: (typeof raw['r'] === 'number' ? raw['r'] : 0) as BodyRotation,
    z_order: typeof raw['z'] === 'number' ? raw['z'] : 0,
    pins: Array.isArray(raw['pn']) ? (raw['pn'] as unknown[]).map(decodePin) : [],
  };
  if (raw['f']) inst.flipped = true;
  if (typeof raw['v'] === 'string') inst.value = raw['v'];
  if (typeof raw['l'] === 'string') inst.label = raw['l'];
  if (isRecord(raw['pr'])) inst.properties = raw['pr'] as Record<string, string>;
  if (isRecord(raw['ao'])) {
    inst.appearance_override = raw['ao'] as NonNullable<ComponentInstance['appearance_override']>;
  }
  return inst;
}

function decodePin(raw: unknown): PinState {
  if (!isRecord(raw)) throw new SerializationError('Estado de pin inválido.');
  const bendsIn = Array.isArray(raw['b']) ? (raw['b'] as unknown[]) : [];
  const bends: BendState[] = bendsIn.map(decodeBend);
  const out: PinState = {
    pin_id: strOr(raw['id'], ''),
    bends,
    cut_at_index: typeof raw['c'] === 'number' ? raw['c'] : null,
  };
  if (typeof raw['dl'] === 'number') out.length_adjust_mm = raw['dl'];
  if (typeof raw['da'] === 'number') out.angle_adjust_deg = raw['da'];
  if (typeof raw['dab'] === 'number') out.angle_adjust_at_bend = raw['dab'];
  if (isRecord(raw['ao'])) {
    const ao: Record<number, number> = {};
    for (const [k, v] of Object.entries(raw['ao'] as Record<string, unknown>)) {
      if (typeof v === 'number') ao[Number(k)] = v;
    }
    out.align_offsets = ao;
  }
  return out;
}

function decodeBend(raw: unknown): BendState {
  if (typeof raw === 'number') return { angle_in_plane_deg: raw, angle_out_of_plane_deg: 0 };
  if (Array.isArray(raw) && typeof raw[0] === 'number') {
    return {
      angle_in_plane_deg: raw[0] as number,
      angle_out_of_plane_deg: typeof raw[1] === 'number' ? (raw[1] as number) : 0,
    };
  }
  return { angle_in_plane_deg: 0, angle_out_of_plane_deg: 0 };
}

// ---------- LEGACY (v1: claves largas) ----------

function decodeLegacyProject(raw: Record<string, unknown>): Project {
  // El v1 guardaba el `Project` tal cual (claves largas).
  if (!isProjectShape(raw)) throw new SerializationError('Proyecto v1 inválido.');
  return raw as Project;
}

function isProjectShape(v: unknown): v is Project {
  return (
    isRecord(v) &&
    typeof v['format_version'] === 'number' &&
    isRecord(v['metadata']) &&
    isRecord(v['canvas']) &&
    Array.isArray(v['components'])
  );
}

// ---------- utilidades ----------

function parseJson(s: string): unknown {
  try {
    return JSON.parse(s);
  } catch (e) {
    throw new SerializationError(`JSON mal formado: ${(e as Error).message}`);
  }
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function isComponentDef(v: unknown): v is ComponentDef {
  return isRecord(v) && typeof v['id'] === 'string' && isRecord(v['body']) && Array.isArray(v['pins']);
}

function strOr(v: unknown, fallback: string): string {
  return typeof v === 'string' ? v : fallback;
}

function nowIso(): string {
  return new Date().toISOString();
}

function defaultCanvas(): CanvasSettings {
  return { units: 'mm', background_color: '#ffffff' };
}

function isDefaultCanvas(c: CanvasSettings): boolean {
  return (
    c.units === 'mm' &&
    (c.background_color === undefined || c.background_color === '#ffffff') &&
    !c.grid
  );
}

// ---------- documentación embebida del mapa de claves ----------

/**
 * Mapa de correspondencia entre claves largas (código TS) y claves
 * cortas (wire format v2). Sirve como documentación y como fuente
 * de verdad para revisiones futuras.
 *
 * Millimeters/Point2D se colapsan a tuplas cuando aparecen como
 * position (p) para ahorrar 2 pares clave/valor por instancia.
 */
export const COMPACT_KEYS = {
  project: {
    format_version: 'v',
    metadata: 'm',
    canvas: 'cv',
    components: 'cs',
    wires: 'ws',
    solder_joints: 'sj',
    embedded_component_defs: 'ed',
  },
  wire: {
    uid: 'u',
    from: 'f',                 // → [x, y]
    direction_deg: 'dd',
    bend_points_count: 'bc',
    bends: 'b',                // número (in-plane) o [in, out]
    cut_at_index: 'c',
    z_order: 'z',
  },
  metadata: {
    name: 'n',
    created_at: 'c',
    modified_at: 'm',
    author: 'a',
    description: 'd',
  },
  instance: {
    uid: 'u',
    def_id: 'd',
    position_mm: 'p',        // → [x, y]
    rotation_deg: 'r',
    z_order: 'z',
    flipped: 'f',            // sólo se emite 1 si true
    value: 'v',
    label: 'l',
    properties: 'pr',
    appearance_override: 'ao',
    pins: 'pn',
  },
  pin_state: {
    pin_id: 'id',
    bends: 'b',              // número (in-plane) o [in, out]
    cut_at_index: 'c',
    length_adjust_mm: 'dl',
    angle_adjust_deg: 'da',
    angle_adjust_at_bend: 'dab',
    align_offsets: 'ao',
  },
} as const;
